import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getInitials } from '../utils/initials';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { NECS_VIOLATION_POLICY } from '../lib/necsViolationPolicy';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Bell, User, LogOut, QrCode, Plus, Clock, CheckCircle, AlertCircle, Package, Image, Trophy, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { getPackageDetails, getFlattenedServices, getCanonicalPackageName } from '../lib/packageDetails';
import PhotoCapture from '../components/PhotoCapture';
import NECSLogo from '../components/NECSLogo';
import PropertyIdCardButton from '../components/PropertyIdCardButton';
import DashboardShell from '../components/DashboardShell';
import WelcomeStrip from '../components/WelcomeStrip';
import TenantEtaCard from '../components/TenantEtaCard';
import TrainingLauncher from '../components/TrainingLauncher';
import LanguageSwitcher from '../components/LanguageSwitcher';
import TenantTrashPlacement from '../components/TenantTrashPlacement';
import { TrashcanRequestFields } from '../components/TrashcanRequestFields';
import { REQUEST_SIGNALS, trashcanDamage, trashcanReplacement } from '../lib/serviceSignals';
import { ServiceSignalsLegend } from '../components/ServiceSignalsLegend';
import { useProfilePhotoSync } from '../hooks/useProfilePhotoSync';
import TenantBulkPickup from '../components/TenantBulkPickup';
import TenantLivePickupCard from '../components/TenantLivePickupCard';
import TenantPendingAssignment from '../components/TenantPendingAssignment';
import AuthorizedUsersCard from '../components/AuthorizedUsersCard';
// Iter-175 · Phase 2 — ResidentRewardBadge removed from header cluster.
import ComplianceTrophy from '../components/ComplianceTrophy';
import TenantServiceHistoryTab from '../components/TenantServiceHistoryTab';
// Iter-163 · Phase 3 — Contracted vs Recommended service packages
// visible on the resident/tenant dashboard for both valet + cleaner
// variants.
import ContractedServicesCard from '../components/ContractedServicesCard';
import RecommendedServicesCard from '../components/RecommendedServicesCard';
import ResidentOperationRecord from './resident/ResidentOperationRecord';
import { roleTermFor, isAuthorizedUser, accountLabelFor } from '../lib/roleTerm';
import UpdatesNoticesPanel, { useUpdatesUnread } from '../components/updates/UpdatesNoticesPanel';
import UpdatesBlockingGate from '../components/updates/UpdatesBlockingGate';
// Iter-169 · Phase 1 — QR scanner dialog (camera + deep-link routing).
import QRScannerDialog from '../components/QRScannerDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TenantDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, loading, logout } = useAuth();
  useProfilePhotoSync(setUser);
  const [activeTab, setActiveTab] = useState('requests');
  const updatesUnread = useUpdatesUnread();
  const [serviceRequests, setServiceRequests] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  // Iter-169 · Phase 3c — contracted services list for this tenant's
  // property. Used to gate the Bulk Item Removal request option.
  const [contractedServices, setContractedServices] = useState([]);
  // Violations filed against this resident's unit — auto-fetched from
  // /api/violations which the backend filters to {reported_by=me OR
  // property_id+unit_number match}. Each item carries the date, photo,
  // description, status, and counts toward the 3-warning charge rule.
  const [violations, setViolations] = useState([]);
  // Iter-163 · Phase 3 — fetch tenant's property so we can render the
  // ContractedServicesCard + RecommendedServicesCard directly.
  const [tenantProperty, setTenantProperty] = useState(null);
  // Dispute dialog state — opened from Violations tab per violation row.
  // Tenants can dispute any violation against their unit by sending a
  // free-text reason and optional supporting photo to the backend, which
  // notifies all NECS admins (in-app + email).
  const [disputeTarget, setDisputeTarget] = useState(null);
  const [disputeForm, setDisputeForm] = useState({ reason: '', photo: null });
  const [disputeBusy, setDisputeBusy] = useState(false);
  const [requestForm, setRequestForm] = useState({
    request_type: 'trash_pickup',
    description: '',
    priority: 'normal',
    photos: [],
    // Iter-171 · Phase 5 — Confirm Trash starts with 0/0 so residents
    // pick a category first, then a quantity. Backend still allows 0.
    trash_bags: 0,
    recycling_bags: 0,
    bulk_items: [],
    placement_confirmed: false,
    trashcan_reason: '', previously_provided: false, replacement_fee_ack: false,
  });
  const [requestBusy, setRequestBusy] = useState(false);
  const requestNeedsPhoto = requestForm.request_type !== 'request_trashcan' || trashcanDamage(requestForm);

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate('/login');
      return;
    }

    // Iter-77 · Only seed from `location.state.user` when the auth
    // context hasn't hydrated yet — otherwise this effect re-runs on
    // every user state change and clobbers fresh updates (e.g. a newly
    // uploaded profile photo) with the stale login-time user object.
    if (!user && location.state?.user) {
      setUser(location.state.user);
    }

    if (user?.role !== 'tenant' && user?.role) {
      navigate('/driver-dashboard');
    }
  }, [user, loading, navigate, location.state, setUser]);

  useEffect(() => {
    if (user) {
      fetchServiceRequests();
      fetchNotifications();
      fetchViolations();
      // Iter-163 · Phase 3 — pull the tenant's property (for
      // ContractedServicesCard + RecommendedServicesCard).
      if (user.property_id) {
        fetch(`${BACKEND_URL}/api/properties/${user.property_id}`, { credentials: 'include' })
          .then((r) => r.ok ? r.json() : null)
          .then((p) => { if (p) setTenantProperty(p); })
          .catch(() => {});
        // Iter-169 · Phase 3c — contracted services for the property so
        // Bulk Item Removal can be enabled/disabled accordingly.
        // Only trust actual contract/property data — the endpoint's
        // "default" fallback (which naively includes bulk_removal)
        // would otherwise mislead the UI into enabling the option
        // even when the property has no real contract on file.
        fetch(`${BACKEND_URL}/api/property/${user.property_id}/contract-services`,
              { credentials: 'include' })
          .then((r) => r.ok ? r.json() : null)
          .then((d) => {
            if (d && Array.isArray(d.services) && d.source !== 'default') {
              setContractedServices(d.services);
            } else {
              setContractedServices([]);
            }
          })
          .catch(() => {});
      }
    }
  }, [user]);

  const fetchServiceRequests = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/service-requests`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setServiceRequests(data);
      }
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/notifications`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const fetchViolations = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/violations`, { credentials: 'include' });
      if (response.ok) setViolations(await response.json());
    } catch (_) { /* ignore */ }
  };

  const handleDisputePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5 MB photo'); return; }
    const reader = new FileReader();
    reader.onload = () => setDisputeForm((f) => ({ ...f, photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const submitDispute = async () => {
    if (!disputeTarget) return;
    if (!disputeForm.reason.trim()) { toast.error('Please describe why you are disputing this violation.'); return; }
    setDisputeBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/violations/${disputeTarget.violation_id || disputeTarget.report_id}/dispute`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: disputeForm.reason.trim(), photo: disputeForm.photo }),
      });
      if (res.ok) {
        toast.success('Dispute sent to NECS — admins notified by app and email.');
        setDisputeTarget(null);
        setDisputeForm({ reason: '', photo: null });
        fetchViolations();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to submit dispute');
      }
    } catch (_) {
      toast.error('Network error — try again');
    } finally {
      setDisputeBusy(false);
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    if (requestBusy) return;
    if (requestForm.request_type === 'request_trashcan' && (!requestForm.trashcan_reason || (trashcanReplacement(requestForm) && !requestForm.replacement_fee_ack))) {
      toast.error('Select a reason and acknowledge any replacement charge.'); return;
    }
    if (requestNeedsPhoto && !(requestForm.photos || []).length) { toast.error('Photo evidence is required.'); return; }

    if (requestForm.request_type === 'bulk_removal') {
      if (!contractedServices.includes('bulk_removal')) {
        toast.error('Bulk Item Removal is not on your current service plan.'); return;
      }
      if (!requestForm.bulk_items || requestForm.bulk_items.length === 0) {
        toast.error('Select at least one bulk item category.'); return;
      }
      if (!requestForm.photos || requestForm.photos.length === 0) {
        toast.error('Placement photo required — items must be visible after staging.'); return;
      }
      if (!requestForm.placement_confirmed) {
        toast.error('Confirm you placed the items near the trash room or dumpster.'); return;
      }
    }

    setRequestBusy(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/service-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestForm)
      });

      if (response.ok) {
        toast.success('Service request submitted!');
        window.dispatchEvent(new CustomEvent('necs:service-signals-changed'));
        setShowRequestDialog(false);
        setRequestForm({
          request_type: 'trash_pickup', description: '', priority: 'normal',
          photos: [], trash_bags: 0, recycling_bags: 0, bulk_items: [],
          placement_confirmed: false,
          trashcan_reason: '', previously_provided: false, replacement_fee_ack: false,
        });
        fetchServiceRequests();
        fetchNotifications();
      } else {
        const data = await response.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : data.detail?.[0]?.msg || 'Failed to create request');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to submit request');
    } finally {
      setRequestBusy(false);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await fetch(`${BACKEND_URL}/api/notifications/${notificationId}/read`, {
        method: 'PATCH',
        credentials: 'include'
      });
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#FFD60A]"></div>
          <p className="mt-4 text-zinc-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  // New Google sign-ups (or any tenant whose unit hasn't been assigned yet)
  // get a friendly welcome screen with next steps instead of a broken dashboard.
  if (user.role === 'tenant' && !user.property_id) {
    return <TenantPendingAssignment user={user} onLogout={logout} />;
  }

  const unreadCount = notifications.filter(n => !n.read).length;
  const packageInfo = getPackageDetails(user.package_type || "Standard Package");

  return (
    <DashboardShell
      brandLabel={`${roleTermFor(user?.property_type)} App`}
      menuItems={[
        { key: 'requests', label: 'Service Requests', icon: AlertCircle },
        { key: 'service_history', label: 'Service History', icon: Clock },
        { key: 'record', label: 'Operation Record', icon: Trophy },
        { key: 'violations', label: 'Violations In/Out', icon: AlertCircle },
        // Red-Team Addendum — Only Account Holders (A/H) can manage
        // Authorized Users. Authorizer Users (A/U = sub-users) don't get
        // this option because they can't add sub-authorizers of their own.
        ...(!isAuthorizedUser(user) ? [{ key: 'authorized', label: 'Authorized Users', icon: User }] : []),
        { key: 'updates', label: 'Updates & Notices', icon: Megaphone, badge: updatesUnread },
        { key: 'notifications', label: 'Notifications', icon: Package },
      ]}
      activeKey={typeof activeTab !== 'undefined' ? activeTab : null}
      onMenuClick={(item) => {
        if (typeof setActiveTab === 'function') setActiveTab(item.key);
        setTimeout(() => document.getElementById('tenant-tabs-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
      }}
      hideUserMenu
      topbarExtras={(
        <>
          <LanguageSwitcher variant="light" testIdPrefix="tenant-lang" />
          <button
            type="button"
            onClick={logout}
            className="p-2 rounded-full hover:bg-black/10 transition"
            title="Logout"
            data-testid="tenant-topbar-logout"
          >
            <LogOut className="h-5 w-5 text-black" />
          </button>
        </>
      )}
    >
    <div className="min-h-full bg-zinc-50 text-zinc-900" data-testid="tenant-dashboard">
      <UpdatesBlockingGate />
      {/* Inner charcoal navbar — mirrors the NECS Admin / StaffDashboard
          layout: profile + notification bell on the right, gold divider
          line aligns at y=146 with the rest of the suite. */}
      <nav className="sticky top-0 z-40" style={{ backgroundColor: '#2D2D2D', borderBottom: '1px solid #C9A962' }} data-testid="tenant-inner-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-2">
              <Badge className="bg-[#C9A962] text-black font-bold tracking-widest text-[10px]">RESIDENT</Badge>
              <span className="text-white text-sm font-semibold truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.04em' }}>
                {user.property_name?.toUpperCase() || 'YOUR COMMUNITY'}
              </span>
            </div>
            {/* Iter-175 · Phase 2 — Trophy → Profile → Bell only.
                ResidentRewardBadge removed from cluster (still available
                on the Profile page). Cluster contains exactly three
                icons at 44×44px, right-aligned. */}
            <div className="flex items-center gap-1 ml-auto pr-1"
                 data-testid="tenant-header-icon-cluster">
              <div style={{ minWidth: 44, minHeight: 44 }}
                   className="flex items-center justify-center"
                   data-testid="topbar-trophy-wrap">
                <div data-testid="tenant-trophy-wrap">
                  <ComplianceTrophy role={user.role} roleType={user.role_type} />
                </div>
              </div>
              <div data-testid="topbar-user-menu-wrap">
              <button
                type="button"
                onClick={() => navigate('/profile')}
                className="rounded-full flex items-center justify-center overflow-hidden text-sm font-bold transition hover:opacity-90 ring-2 ring-[#C9A962] ring-offset-2 ring-offset-zinc-950 shadow-lg shadow-[#C9A962]/25"
                style={{
                  width: 44, height: 44,
                  backgroundColor: (user.profile_photo || user.picture) ? 'transparent' : '#C9A962',
                  color: '#0a0a0a',
                  border: '2px solid #C9A962',
                }}
                data-testid="tenant-profile-btn"
                title="Profile"
                aria-label="Profile"
              >
                {(user.profile_photo || user.picture) ? (
                  <img
                    src={user.profile_photo || user.picture}
                    alt=""
                    className="w-full h-full object-cover"
                    data-testid="tenant-profile-photo"
                  />
                ) : (
                  <span data-testid="tenant-profile-initials">{getInitials(user.name || user.email)}</span>
                )}
              </button>
              </div>
              <div data-testid="topbar-bell-wrap">
              <button
                type="button"
                className="relative text-white hover:text-[#C9A962] rounded-full hover:bg-white/5 transition flex items-center justify-center"
                style={{ width: 44, height: 44 }}
                onClick={() => {
                  setActiveTab?.('notifications');
                  setTimeout(() => document.getElementById('tenant-tabs-anchor')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
                }}
                data-testid="tenant-bell"
                title="Notifications"
                aria-label="Notifications"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute top-1 right-1 h-4 min-w-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <WelcomeStrip
          name={user.name || 'Resident'}
          subtitle={`${accountLabelFor(user)} · ${user.property_name ? user.property_name + ' · ' : ''}Unit ${user.unit_number || 'N/A'} · ${user.package_type || 'Standard Package'}`}
          testid="tenant-welcome-strip"
          welcomeLabel={`Welcome ${roleTermFor(user?.property_type)}s`}
        />

        {/* Iter-190 · My Property/Facility ID QR card — one-tap wallet
            card the resident/tenant can save + present to on-site staff. */}
        {(user?.property_code || user?.property_necs_code) && (
          <PropertyIdCardButton
            propertyCode={user.property_code || user.property_necs_code}
            propertyName={user.property_name}
          />
        )}

        {/* Iter-91 · Live ETA card — matches the ETA the Ops Live Route
            map is showing, property-scoped server-side. When no route is
            active, shows the next scheduled pickup date instead. */}
        <div className="mb-6">
          <TenantEtaCard />
        </div>

        {/* Three-photo tenant snapshot strip — full-bleed photos sitting
            close to each other tell the resident-side story end-to-end:
            (1) doorstep pickup at unit 1203, (2) the older couple at 2408
            placing their bag, (3) the resident + NECS agent comparing
            apps at door 203. Each image owns its own case but they share
            a single header card so they read as a triptych. */}
        <Card className="overflow-hidden mb-6 border border-zinc-200 bg-zinc-900" data-testid="tenant-service-banner">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-1 bg-zinc-900">
            <img
              src="https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/80491j1n_1927.png"
              alt="NECS agent in branded gear scanning a doorstep pickup at unit 1203 with a Noble Empire-labeled bin and bag"
              className="w-full h-56 sm:h-72 object-cover object-center"
              data-testid="tenant-snapshot-photo-1"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/ty6u760b_1925.png"
              alt="Resident at unit 2408 taking out a Noble Empire-branded trash bin while his partner watches from the couch"
              className="w-full h-56 sm:h-72 object-cover object-center"
              data-testid="tenant-snapshot-photo-2"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_e7894913-6ae5-4a5a-877c-691d11e0efaf/artifacts/e8t6sji8_1645.jpg"
              alt="NECS Noble Empire agent and a resident comparing app screens at door 203 — both showing the NECS app"
              className="w-full h-56 sm:h-72 object-cover object-center"
              data-testid="tenant-snapshot-photo-3"
            />
          </div>
          <div className="px-5 sm:px-7 py-4 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 55%, #2a2010 100%)' }} data-testid="tenant-service-banner-overlay">
            <div className="absolute left-0 right-0 top-0 h-[2px]" style={{ background: 'linear-gradient(to right, transparent, #C9A962 30%, #FFD60A 50%, #C9A962 70%, transparent)' }} />
            <p
              className="text-[10px] uppercase tracking-[0.3em] mb-1.5"
              style={{ color: '#C9A962', fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 600 }}
            >
              Noble Empire · Resident Promise
            </p>
            <h2
              className="necs-dashboard-caption text-base sm:text-lg leading-snug"
              style={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontStyle: 'italic',
                fontWeight: 600,
                color: '#ffffff',
                textShadow: '0 1px 6px rgba(0,0,0,0.5)',
              }}
              data-testid="tenant-snapshot-tagline"
            >
              Setting the Standard for Property Care, Community Cleanliness &amp; Operational Excellence.
            </h2>
            <p className="text-xs text-white/60 mt-1 italic" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
              Tie · place outside your door · NECS handles the rest.
            </p>
          </div>
        </Card>

        {/* SERVICE PACKAGE — fully flattened bullet list so residents see
            every single service their property's tier covers. Inherited
            "Everything in X Collection" lines are auto-expanded by
            getFlattenedServices() in packageDetails.js. */}
        <Card
          className="p-6 mb-8 border-2 relative overflow-hidden"
          style={{
            borderColor: '#C9A962',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #2D2D2D 65%, #3a2f15 100%)',
          }}
          data-testid="tenant-package-card"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 rounded-lg" style={{ backgroundColor: '#C9A962' }}>
              <Package className="h-7 w-7 text-black" />
            </div>
            <h2
              className="text-2xl sm:text-4xl font-extrabold uppercase tracking-wide leading-tight"
              style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                color: '#FFD60A',
                textShadow: '0 2px 6px rgba(0,0,0,0.5)',
                letterSpacing: '0.05em',
              }}
              data-testid="tenant-package-name"
            >
              {getCanonicalPackageName(user.package_type)}
            </h2>
          </div>
          <p className="text-xs text-zinc-300 mb-4">
            Schedule · <strong className="text-[#C9A962]">{packageInfo.shift}</strong>
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5" data-testid="tenant-package-services">
            {getFlattenedServices(user.package_type).map((service, idx) => (
              <li key={idx} className="flex items-start text-sm text-white">
                <CheckCircle className="h-4 w-4 text-[#C9A962] mr-2 flex-shrink-0 mt-0.5" />
                <span>{service}</span>
              </li>
            ))}
          </ul>
          <p className="text-[11px] text-zinc-400 mt-4 italic">
            * Every service is listed for transparency — no &ldquo;Everything in X&rdquo; placeholders.
            Package selected by your property management for {user.property_name || 'your community'}.
          </p>
        </Card>

        {/* Uber Trash — resident check-in */}
        <div className="mb-8">
          <TenantTrashPlacement />
        </div>

        {/* Iter-77 · Service signals legend — resident-facing so they
            understand what the crew sees when they arrive. */}
        <div className="mb-6">
          <ServiceSignalsLegend
            propertyId={user?.property_id}
            showCounts={false}
          />
        </div>

        {/* Iter-171 · Phase 6 — Contracted Services + Recommended
            Services panels removed from the resident dashboard per
            product direction. Space collapses cleanly. */}

        {/* Live pickup tracking — only renders when a driver is actively running a route to this tenant's property */}
        <div className="mb-6">
          <TenantLivePickupCard />
        </div>

        {/* Bulk Pickup — only renders if property opted in */}
        <TenantBulkPickup />

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6 border-2 border-zinc-200 hover:border-[#FFD60A] transition-all">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>ACTIVE REQUESTS</h3>
              <AlertCircle className="h-6 w-6 text-[#FFD60A]" />
            </div>
            <p className="text-3xl font-black">{serviceRequests.filter(r => r.status !== 'completed').length}</p>
          </Card>

          <Card className="p-6 border-2 border-zinc-200 hover:border-[#FFD60A] transition-all">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>COMPLETED</h3>
              <CheckCircle className="h-6 w-6 text-green-500" />
            </div>
            <p className="text-3xl font-black">{serviceRequests.filter(r => r.status === 'completed').length}</p>
          </Card>

          <Card className="p-6 border-2 border-zinc-200 hover:border-[#FFD60A] transition-all">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>NEXT PICKUP</h3>
              <Clock className="h-6 w-6 text-blue-500" />
            </div>
            <p className="text-xl font-bold">Mon-Fri 8PM</p>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
            <DialogTrigger asChild>
              <Card className="p-8 border-2 border-[#FFD60A] bg-[#FFD60A] hover:bg-[#EAB308] transition-all cursor-pointer" data-testid="create-request-card">
                <div className="flex items-center justify-center space-x-4">
                  <Plus className="h-8 w-8 text-black" />
                  <h3 className="text-2xl font-bold text-black" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                    REQUEST SERVICE
                  </h3>
                </div>
              </Card>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto bg-white text-black border-4 border-black"
                           data-testid="create-service-request-panel">
              <DialogHeader>
                <DialogTitle className="text-lg font-bold text-black">Create Service Request</DialogTitle>
                <DialogDescription className="sr-only">Service request details, priority and photo evidence.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateRequest} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-base font-bold text-black">Request Type</Label>
                  {/* Iter-172 · Phase 4 + Iter-173 · Phase 2 — brand chip
                      selector: yellow selected / white unselected. */}
                  <div className="flex flex-wrap gap-2" role="radiogroup"
                       data-testid="request-type-chip-group">
                    {[
                      { k: 'request_trashcan',      l: 'Request Trashcan' },
                      { k: 'trash_pickup',          l: 'Trash Pickup' },
                      { k: 'missed_pickup',         l: 'Missed Pickup' },
                      { k: 'violation_report',      l: 'Violation Report' },
                      { k: 'bulk_removal',          l: 'Bulk Item Removal',
                        disabled: !contractedServices.includes('bulk_removal') },
                      { k: 'other',                 l: 'Other' },
                    ].map((opt) => {
                      const active = requestForm.request_type === opt.k;
                      const testId = opt.k === 'bulk_removal'
                          ? (opt.disabled ? 'request-type-bulk-removal-disabled' : 'request-type-bulk-removal')
                          : `request-type-${opt.k.replace(/_/g,'-')}`;
                      return (
                        <button
                          type="button"
                          key={opt.k}
                          disabled={opt.disabled}
                          onClick={() => setRequestForm({ ...requestForm, request_type: opt.k })}
                          className="px-3 py-2 rounded-lg text-sm font-bold border-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{
                            backgroundColor: active ? REQUEST_SIGNALS[opt.k].color : '#ffffff',
                            color: active ? REQUEST_SIGNALS[opt.k].text : '#0a0a0a',
                            borderColor: REQUEST_SIGNALS[opt.k].color,
                          }}
                          data-testid={testId}
                          role="radio"
                          aria-checked={active}
                        >
                          {opt.l}{opt.disabled ? ' (not on plan)' : ''}
                        </button>
                      );
                    })}
                  </div>
                  <input type="hidden" value={requestForm.request_type}
                         data-testid="request-type-hidden" readOnly />
                  <p className="text-sm text-black font-bold mt-1"
                     data-testid="request-type-hint">
                    Selected: <span className="uppercase">{requestForm.request_type.replace(/_/g,' ')}</span>
                  </p>
                </div>
                {/* Iter-172 · Phase 4 fix — legacy Radix Select removed;
                    the hidden request-type-select node used by older
                    tests is now a plain <input> so onValueChange('')
                    can no longer wipe state. */}
                <input type="hidden" data-testid="request-type-select"
                       value={requestForm.request_type} readOnly />

                {requestForm.request_type === 'request_trashcan' && <TrashcanRequestFields value={requestForm} onChange={setRequestForm} />}

                {/* Iter-169 · Phase 3c — Bulk item picker + placement
                    disclosure. Only rendered when the resident has
                    Bulk Item Removal on their contract. */}
                {requestForm.request_type === 'bulk_removal' && contractedServices.includes('bulk_removal') && (
                  <div className="rounded-lg border-2 border-gray-400 bg-gray-100 p-4 space-y-3"
                       data-testid="bulk-removal-panel">
                    <p className="text-sm font-bold text-gray-900">Bulk Item Removal</p>
                    <div>
                      <Label className="text-sm">Items to remove (tap any that apply)</Label>
                      <div className="mt-2 grid grid-cols-2 gap-1.5" data-testid="bulk-items-grid">
                        {['sofa','mattress','box_spring','dresser','table','chairs',
                          'refrigerator','washer_dryer','tv','boxes_cardboard','other'].map((item) => {
                          const selected = requestForm.bulk_items.includes(item);
                          return (
                            <button
                              type="button"
                              key={item}
                              onClick={() => setRequestForm((f) => ({
                                ...f,
                                bulk_items: selected
                                  ? f.bulk_items.filter((i) => i !== item)
                                  : [...f.bulk_items, item],
                              }))}
                              className="px-2 py-1.5 rounded text-xs font-semibold border transition-colors"
                              style={{
                                backgroundColor: selected ? '#9ca3af' : '#ffffff',
                                color: '#18181b',
                                borderColor: '#9ca3af',
                              }}
                              data-testid={`bulk-item-${item}`}
                            >
                              {item.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="rounded border-2 border-red-400 bg-red-50 p-3 text-sm text-red-900"
                         data-testid="bulk-placement-notice">
                      <strong>Important placement notice:</strong> All bulk items must be placed
                      <em> near the side </em>of the trash room or dumpster — <strong>not inside</strong>.
                      Photos must be taken <strong>after placement</strong> to confirm positioning.
                      Items blocking the waste operator&apos;s path will not be collected.
                    </div>
                    <label className="flex items-start gap-2 text-sm text-blue-900 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={requestForm.placement_confirmed}
                        onChange={(e) => setRequestForm({ ...requestForm, placement_confirmed: e.target.checked })}
                        className="mt-0.5"
                        data-testid="bulk-placement-confirm"
                      />
                      <span>I have staged the items near the trash room / dumpster and the photo below
                        confirms placement.</span>
                    </label>
                  </div>
                )}

                {(
                  <div>
                    <Label className="text-base font-bold text-black">Description</Label>
                    <Textarea
                      value={requestForm.description}
                      onChange={(e) => setRequestForm({...requestForm, description: e.target.value})}
                      required={requestForm.request_type !== 'request_trashcan'}
                      data-testid="request-description"
                      className="mt-1 text-black bg-white border-2 border-black focus-visible:ring-[#FFD60A] placeholder:text-zinc-500"
                      placeholder="Tell us what's going on…"
                    />
                  </div>
                )}

                <div>
                  <Label className="text-base font-bold text-black">Priority</Label>
                  {/* Iter-173 · Phase 2 — chip-based priority with
                      color-coded selected states + hidden Radix mirror
                      for backwards-compat tests. */}
                  <div className="mt-2 flex flex-wrap gap-2" role="radiogroup"
                       data-testid="request-priority-chip-group">
                    {[
                      { k: 'low',    l: 'Low',    color: '#6b7280' },
                      { k: 'normal', l: 'Normal', color: '#111827' },
                      { k: 'high',   l: 'High',   color: '#d97706' },
                      { k: 'urgent', l: 'Urgent', color: '#dc2626' },
                    ].map((p) => {
                      const active = requestForm.priority === p.k;
                      return (
                        <button
                          type="button"
                          key={p.k}
                          onClick={() => setRequestForm({ ...requestForm, priority: p.k })}
                          className="px-4 py-2 rounded text-sm font-bold border-2 transition-colors"
                          style={{
                            backgroundColor: active ? '#FFD60A' : '#ffffff',
                            color: active ? '#0a0a0a' : p.color,
                            borderColor: active ? '#0a0a0a' : '#d1d5db',
                            borderBottomWidth: active ? '4px' : '2px',
                          }}
                          data-testid={`request-priority-${p.k}`}
                          role="radio"
                          aria-checked={active}
                        >
                          {p.l}
                        </button>
                      );
                    })}
                  </div>
                  <input type="hidden" data-testid="request-priority-select"
                         value={requestForm.priority} readOnly />
                </div>

                <div>
                  <Label className="text-base font-bold text-black flex items-center">
                    <Image className="h-4 w-4 mr-2 text-[#C9A962]" />
                    Photo Evidence{' '}
                    {!requestNeedsPhoto
                      ? <span>(Optional)</span>
                      : <span className="text-red-600">(Required)</span>}
                  </Label>
                  <p className="text-[13px] text-black/70 mt-0.5">
                    {(requestForm.photos || []).length} / 3 photos · Max 5MB per photo
                  </p>
                  <div className="mt-2">
                    <PhotoCapture
                      testIdPrefix="request"
                      onPhotosChange={(photos) => setRequestForm({...requestForm, photos})}
                      maxPhotos={3}
                      existingPhotos={requestForm.photos}
                    />
                  </div>
                  {/* Iter-192b · Thumbnail preview grid — the resident
                      sees every photo they attached BEFORE submit so
                      they never send a Violation Report or Bulk
                      Removal request without a working image. */}
                  {(requestForm.photos || []).length > 0 && (
                    <div className="mt-2 grid grid-cols-3 gap-2" data-testid="request-photo-previews">
                      {(requestForm.photos || []).map((p, idx) => {
                        const src = typeof p === 'string' ? p : (p?.data || p?.data_url || p?.url || p?.dataUrl || '');
                        if (!src) return null;
                        return (
                          <div key={idx} className="relative rounded-md overflow-hidden border-2 border-black/70 bg-zinc-100">
                            <img src={src} alt={`Evidence ${idx + 1}`} className="w-full h-24 object-cover" data-testid={`request-photo-preview-${idx}`} />
                            <span className="absolute top-1 left-1 bg-black/70 text-white text-[10px] font-bold uppercase tracking-widest rounded px-1.5 py-0.5">
                              {idx + 1}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={
                    // Iter-192 · Description is only required when NOT
                    // Confirm Trash Outside (that type is category-only).
                    requestBusy || (requestForm.request_type !== 'request_trashcan'
                      && !requestForm.description.trim())
                    // Confirm Trash Outside needs at least one category picked.
                    || (requestForm.request_type === 'request_trashcan'
                        && (!requestForm.trashcan_reason || !requestForm.trashcan_eligibility_loaded || requestForm.trashcan_active_request || (trashcanReplacement(requestForm) && !requestForm.replacement_fee_ack)))
                    // Bulk removal — items + placement confirmation + photo.
                    || (requestForm.request_type === 'bulk_removal'
                        && (!contractedServices.includes('bulk_removal')
                            || (requestForm.bulk_items || []).length === 0
                            || (requestForm.photos || []).length === 0
                            || !requestForm.placement_confirmed))
                    // Iter-192b · Photo REQUIRED for every other type
                    // except the daily Confirm Trash Outside check-in.
                    || (requestNeedsPhoto && (requestForm.photos || []).length === 0)
                  }
                  className="w-full font-bold text-base py-3 border-2 transition-colors disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: '#0a0a0a',
                    color: '#ffffff',
                    borderColor: '#0a0a0a',
                  }}
                  data-testid="submit-request-button"
                >
                  {requestBusy ? 'Submitting…' : 'Submit Request'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Card
            className="p-8 border-2 border-zinc-900 bg-zinc-900 hover:bg-zinc-800 transition-all cursor-pointer"
            data-testid="scan-qr-card"
            role="button"
            tabIndex={0}
            onClick={() => setShowQRDialog(true)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowQRDialog(true); } }}
          >
            <div className="flex items-center justify-center space-x-4">
              <QrCode className="h-8 w-8 text-[#FFD60A]" />
              <h3 className="text-2xl font-bold text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                SCAN QR CODE
              </h3>
            </div>
          </Card>
        </div>

        {/* Iter-169 · Phase 1 — QR scanner dialog. Full camera flow
            with permission-denial and error branches. */}
        <QRScannerDialog
          open={showQRDialog}
          onOpenChange={setShowQRDialog}
          onResult={(payload) => toast.success(`Scanned: ${String(payload).slice(0, 40)}`)}
        />

        {user.qr_code && (
          <Card className="p-6 mb-8">
            <h3 className="text-xl font-bold mb-4" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>YOUR QR CODE</h3>
            <div className="flex justify-center">
              <img src={`data:image/png;base64,${user.qr_code}`} alt="Unit QR Code" className="w-48 h-48" />
            </div>
            <p className="text-center text-sm text-zinc-600 mt-2">Attach this to your trash can</p>
          </Card>
        )}

        {/* Pre-pickup violation-policy alert — a compact reminder card
            shown to every resident so they know exactly what our agents
            will flag when they walk the property tonight. */}
        <Card
          className="p-4 mb-6 bg-amber-50 border-2 border-amber-400"
          data-testid="tenant-prepickup-alert"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="font-bold text-amber-900 text-sm">
                Before you put your trash out tonight — quick checklist
              </p>
              <ul className="mt-1 text-[12px] text-amber-950 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 list-disc pl-4">
                {NECS_VIOLATION_POLICY.map((v) => (
                  <li key={v.key} data-testid={`tenant-prepickup-item-${v.key}`}>
                    {v.label}
                  </li>
                ))}
              </ul>
              <p className="text-[10px] text-amber-800 mt-1.5">
                Tap <em>Violations In/Out</em> in the sidebar for the full policy detail.
              </p>
            </div>
          </div>
        </Card>

        <div id="tenant-tabs-anchor" className="scroll-mt-24" />
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          {/* Iter-194 · Sidebar mirrors this exact tab order so the
              dashboard reads the same top-to-bottom whether the user
              scans the left rail or the top strip. */}
          <TabsList className="flex flex-wrap gap-1 h-auto p-1 justify-start"
                    data-testid="tenant-tabs-list">
            <TabsTrigger value="requests" data-testid="tab-requests"
                         className="text-sm font-semibold px-4 py-2 h-10">
              Service Requests
            </TabsTrigger>
            <TabsTrigger value="service_history" data-testid="tab-service-history"
                         className="text-sm font-semibold px-4 py-2 h-10">
              Service History
            </TabsTrigger>
            <TabsTrigger value="record" data-testid="tab-operation-record"
                         className="text-sm font-semibold px-4 py-2 h-10">
              Operation Record
            </TabsTrigger>
            <TabsTrigger value="violations" data-testid="tab-violations"
                         className="text-sm font-semibold px-4 py-2 h-10">
              Violations In/Out
            </TabsTrigger>
            {/* Red-Team Addendum — Authorized Users tab is Account-Holder only. */}
            {!isAuthorizedUser(user) && (
              <TabsTrigger value="authorized" data-testid="tab-authorized-users"
                           className="text-sm font-semibold px-4 py-2 h-10">
                Authorized Users
              </TabsTrigger>
            )}
            <TabsTrigger value="updates" data-testid="tab-updates"
                         className="text-sm font-semibold px-4 py-2 h-10">
              Updates &amp; Notices{updatesUnread > 0 ? ` (${updatesUnread})` : ''}
            </TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-notifications"
                         className="text-sm font-semibold px-4 py-2 h-10">
              Notifications{unreadCount > 0 ? ` (${unreadCount})` : ''}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="requests" className="space-y-4">
            {serviceRequests.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-zinc-600">No service requests yet</p>
              </Card>
            ) : (
              serviceRequests.map((request) => (
                <Card key={request.request_id} className="p-6" data-testid="service-request-item">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="font-bold text-lg">{request.request_type.replace('_', ' ').toUpperCase()}</h3>
                        <Badge className={getStatusColor(request.status)}>{request.status}</Badge>
                        {(request.priority === 'high' || request.priority === 'urgent') && (
                          <Badge className="bg-red-100 text-red-800 border-red-200">{request.priority}</Badge>
                        )}
                      </div>
                      <p className="text-zinc-600 mb-2">{request.description}</p>
                      {request.photos && request.photos.length > 0 && (
                        <div className="flex space-x-2 mb-2">
                          {request.photos.slice(0, 3).map((photo, idx) => (
                            <img
                              key={idx}
                              src={photo.data}
                              alt={`Evidence ${idx + 1}`}
                              className="h-16 w-16 object-cover rounded border border-zinc-300 cursor-pointer hover:opacity-80"
                              onClick={() => window.open(photo.data, '_blank')}
                            />
                          ))}
                          {request.photos.length > 3 && (
                            <div className="h-16 w-16 bg-zinc-200 rounded flex items-center justify-center text-xs text-zinc-600">
                              +{request.photos.length - 3} more
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex items-center space-x-4 text-sm text-zinc-500">
                        <span className="font-mono">#{request.request_id}</span>
                        <span>{new Date(request.created_at).toLocaleDateString()}</span>
                        {request.assigned_driver && <span>Driver: {request.assigned_driver}</span>}
                      </div>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="service_history" className="space-y-4">
            <TenantServiceHistoryTab />
          </TabsContent>

          <TabsContent value="notifications" className="space-y-4">
            {notifications.length === 0 ? (
              <Card className="p-8 text-center">
                <p className="text-zinc-600">No notifications</p>
              </Card>
            ) : (
              notifications.map((notif) => (
                <Card 
                  key={notif.notification_id} 
                  className={`p-6 cursor-pointer text-zinc-900 ${!notif.read ? 'border-l-4 border-l-[#FFD60A] bg-amber-50' : 'bg-white'}`}
                  onClick={() => !notif.read && markNotificationRead(notif.notification_id)}
                  data-testid="notification-item"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-bold mb-1 break-words">{notif.title}</h3>
                      <p className="text-zinc-600 text-sm break-words">{notif.message}</p>
                      <p className="text-xs text-zinc-500 mt-2">{new Date(notif.created_at).toLocaleString()}</p>
                    </div>
                    {!notif.read && (
                      <div className="h-2 w-2 bg-[#FFD60A] rounded-full"></div>
                    )}
                  </div>
                </Card>
              ))
            )}
          </TabsContent>

          {!isAuthorizedUser(user) && (
            <TabsContent value="authorized" className="space-y-4">
              <AuthorizedUsersCard />
            </TabsContent>
          )}

          {/* Operation Record — same Trophy screen as the Resident PWA */}
          <TabsContent value="record" className="space-y-4">
            <div className="rounded-2xl p-5 max-w-xl" style={{ backgroundColor: '#141414' }} data-testid="tenant-operation-record">
              <ResidentOperationRecord />
            </div>
          </TabsContent>

          {/* Updates & Notices (Training preserved as a sub-tab) */}
          <TabsContent value="updates" className="space-y-4">
            <UpdatesNoticesPanel testIdPrefix="tenant-updates"
              trainingSlot={<TrainingLauncher dataTestId="tenant-training-launcher" />} />
          </TabsContent>

          {/* Violations In / Out — full transparency for the resident:
              every entry shows the date, the violation type, the photo
              the agent captured, the agent's description, and where the
              unit currently sits on the 3-warning charge ladder. */}
          <TabsContent value="violations" className="space-y-4">
            {(() => {
              const againstMe = violations.filter(
                (v) => v.unit_number && v.unit_number === user?.unit_number,
              );
              const activeAgainstMe = againstMe.filter((v) => v.status !== 'resolved' && v.status !== 'dismissed');
              const warningCount = activeAgainstMe.length;
              const tier =
                warningCount >= 3 ? { color: '#dc2626', label: 'CHARGE PENDING' }
                : warningCount === 2 ? { color: '#ea580c', label: 'FINAL WARNING' }
                : warningCount === 1 ? { color: '#eab308', label: 'FIRST WARNING' }
                : { color: '#16a34a', label: 'CLEAR' };
              return (
                <>
                  <Card className="p-5 border-2" style={{ borderColor: tier.color }} data-testid="tenant-violations-summary">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <h3 className="font-bold text-lg flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                        <AlertCircle className="h-5 w-5" style={{ color: tier.color }} />
                        VIOLATIONS · UNIT {user?.unit_number || '—'}
                      </h3>
                      <Badge className="text-white text-xs font-bold" style={{ backgroundColor: tier.color }} data-testid="tenant-violations-tier">
                        {tier.label} · {warningCount}/3
                      </Badge>
                    </div>
                    <p className="text-xs text-zinc-600 leading-relaxed">
                      Three or more active violations against your unit trigger a charge on your next invoice
                      under your property&rsquo;s violation policy. Each item below shows what happened, the date,
                      the photo our agent captured, and current status. Resolve issues with your property
                      manager to clear active warnings.
                    </p>
                  </Card>

                  <Card className="p-4 bg-amber-50 border-2 border-amber-400" data-testid="tenant-violation-policy-card">
                    <h4 className="font-bold text-sm text-amber-900 mb-2 flex items-center gap-1.5">
                      <AlertCircle className="h-4 w-4 text-amber-700" />
                      NECS Violation Policy — What agents are looking for
                    </h4>
                    <ol className="space-y-1.5 text-[13px] text-amber-950 list-decimal pl-5">
                      {NECS_VIOLATION_POLICY.map((v) => (
                        <li key={v.key} data-testid={`tenant-policy-${v.key}`}>
                          <strong>{v.label}.</strong>{' '}
                          <span className="text-amber-900/90">{v.tenant_facing}</span>
                        </li>
                      ))}
                    </ol>
                    <p className="text-[11px] text-amber-800 mt-2">
                      You receive a push notification the evening before every scheduled pickup so you can double-check the six items above.
                    </p>
                  </Card>

                  <Card className="p-4 bg-blue-50 border border-blue-200" data-testid="tenant-violations-notice">
                    <p className="text-sm text-blue-900 leading-relaxed">
                      <strong>How you&rsquo;ll hear about new violations:</strong> every time an agent files
                      a violation against your unit you receive the same record three ways — a push
                      notification right here in the app (badge on the bell), an email to the address
                      on file, and a text message to your phone. You don&rsquo;t have to be logged in to
                      know — but you&rsquo;ll always find the full history with photos in this tab.
                    </p>
                  </Card>

                  {againstMe.length === 0 ? (
                    <Card className="p-6 text-center">
                      <CheckCircle className="h-10 w-10 mx-auto text-green-500 mb-2" />
                      <p className="font-bold">No violations against your unit.</p>
                      <p className="text-xs text-zinc-500">Keep it up — clean record means no surprise fees.</p>
                    </Card>
                  ) : (
                    <div className="space-y-3" data-testid="tenant-violations-list">
                      {againstMe.map((v) => {
                        const photo = (v.photos && v.photos[0]) || v.photo_url || v.image_url;
                        const isResolved = v.status === 'resolved' || v.status === 'dismissed';
                        return (
                          <Card
                            key={v.violation_id || v.report_id}
                            className="p-4 border"
                            style={{ borderColor: isResolved ? '#a1a1aa' : (tier.color + '80') }}
                            data-testid={`tenant-violation-${v.violation_id || v.report_id}`}
                          >
                            <div className="flex gap-4">
                              {photo && (
                                <img
                                  src={photo}
                                  alt="Violation evidence"
                                  className="h-24 w-24 sm:h-28 sm:w-28 object-cover rounded border border-zinc-200 flex-shrink-0"
                                  data-testid={`tenant-violation-photo-${v.violation_id || v.report_id}`}
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between flex-wrap gap-1">
                                  <p className="font-bold text-sm">{v.violation_type || 'Violation'}</p>
                                  <Badge
                                    className="text-[10px]"
                                    style={{
                                      backgroundColor: isResolved ? '#10b981' : tier.color,
                                      color: '#fff',
                                    }}
                                  >
                                    {(v.status || 'active').replace('_', ' ').toUpperCase()}
                                  </Badge>
                                </div>
                                <p className="text-[11px] text-zinc-500 mt-0.5">
                                  {v.created_at ? new Date(v.created_at).toLocaleString() : '—'}
                                  {v.reporter_name ? <span> · by {v.reporter_name}</span> : null}
                                </p>
                                {v.description && (
                                  <p className="text-xs text-zinc-700 mt-2 leading-relaxed">{v.description}</p>
                                )}
                                {v.ai_analysis && (
                                  <p className="text-[11px] text-zinc-500 mt-1 italic">AI: {v.ai_analysis}</p>
                                )}
                                {v.dispute && (
                                  <div className="mt-2 p-2 rounded border border-amber-300 bg-amber-50">
                                    <p className="text-[11px] font-bold text-amber-900">DISPUTE FILED · awaiting admin review</p>
                                    <p className="text-[11px] text-amber-800 mt-0.5">{v.dispute.reason}</p>
                                  </div>
                                )}
                                {!isResolved && !v.dispute && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="mt-3 h-7 text-[11px] border-amber-500 text-amber-700 hover:bg-amber-50"
                                    onClick={() => setDisputeTarget(v)}
                                    data-testid={`tenant-violation-dispute-${v.violation_id || v.report_id}`}
                                  >
                                    <AlertCircle className="h-3.5 w-3.5 mr-1" /> Dispute this violation
                                  </Button>
                                )}
                              </div>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })()}
          </TabsContent>
        </Tabs>
      </div>

      {/* Tenant dispute dialog — opens from each violation row. Sends a
          free-text reason + optional photo to /api/violations/:id/dispute
          which flags the violation as `disputed` and notifies all NECS
          admins via the in-app inbox AND Resend email. */}
      <Dialog open={!!disputeTarget} onOpenChange={(o) => { if (!o) { setDisputeTarget(null); setDisputeForm({ reason: '', photo: null }); } }}>
        <DialogContent className="sm:max-w-md" data-testid="tenant-dispute-dialog">
          <DialogHeader>
            <DialogTitle>Dispute Violation</DialogTitle>
          </DialogHeader>
          {disputeTarget && (
            <div className="space-y-3">
              <div className="rounded p-3 bg-zinc-50 border border-zinc-200">
                <p className="text-xs text-zinc-500">You are disputing:</p>
                <p className="font-bold text-sm">{disputeTarget.violation_type}</p>
                <p className="text-[11px] text-zinc-600 mt-0.5">{disputeTarget.description}</p>
              </div>
              <div>
                <Label htmlFor="dispute-reason">Why is this incorrect?</Label>
                <Textarea
                  id="dispute-reason"
                  rows={4}
                  value={disputeForm.reason}
                  onChange={(e) => setDisputeForm((f) => ({ ...f, reason: e.target.value }))}
                  placeholder="Explain in your own words — e.g. wrong unit, photo taken before your trash was placed, etc."
                  data-testid="tenant-dispute-reason"
                />
              </div>
              <div>
                <Label htmlFor="dispute-photo">Supporting photo (optional)</Label>
                <Input id="dispute-photo" type="file" accept="image/*" onChange={handleDisputePhoto} data-testid="tenant-dispute-photo" />
                {disputeForm.photo && (
                  <img src={disputeForm.photo} alt="Dispute evidence" className="mt-2 h-24 w-24 object-cover rounded border" />
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setDisputeTarget(null)} data-testid="tenant-dispute-cancel">Cancel</Button>
                <Button
                  onClick={submitDispute}
                  disabled={disputeBusy}
                  className="bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold"
                  data-testid="tenant-dispute-submit"
                >
                  {disputeBusy ? 'Sending…' : 'Send Dispute'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </DashboardShell>
  );
}
